import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  patchDirect, patchAi, commitPatch, checkoutRev,
  nodeAt, targetPath, targetLabel,
  type Session, type Target, type PatchOp, type PatchResult,
} from '../lib/collateral';

/**
 * The one-pager edit loop. The composed HTML renders in a same-origin iframe;
 * clicking a block resolves to its data-block-idx/data-field-id (native DOM
 * hit-test). Edits (direct controls or an AI "describe a change") preview before
 * they commit, over an append-only revision log you can step back through.
 */
export default function OnePagerEditor({
  session, onChange, onExit,
}: {
  session: Session;
  onChange: (s: Session) => void;
  onExit: () => void;
}) {
  const [target, setTarget] = useState<Target | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [pending, setPending] = useState<PatchResult | null>(null);
  const [busy, setBusy] = useState<'apply' | 'ask' | 'commit' | 'checkout' | null>(null);
  const [describe, setDescribe] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // The HTML shown: a pending preview if one exists, else the committed HEAD.
  const html = pending?.html ?? session.html;
  const doc = pending?.doc ?? session.doc;

  const closePopover = useCallback(() => { setTarget(null); setPos(null); setDescribe(''); setErr(null); }, []);
  const discardPending = useCallback(() => { setPending(null); closePopover(); }, [closePopover]);

  // Wire click-to-anchor into the iframe once its content loads.
  const onIframeLoad = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    const idoc = iframeRef.current?.contentDocument;
    if (!win || !idoc) return;
    const style = idoc.createElement('style');
    style.textContent = `
      [data-block-idx],[data-field-id]{cursor:pointer;transition:outline .1s ease, background .1s ease;}
      [data-block-idx]:hover,[data-field-id]:hover{outline:2px solid rgba(138,27,27,.55);outline-offset:3px;background:rgba(138,27,27,.05);}
      .mz-selected{outline:2px solid #8a1b1b !important;outline-offset:3px;}
      body{cursor:default;}
    `;
    idoc.head.appendChild(style);
    idoc.addEventListener('click', (e) => {
      const el = (e.target as Element | null)?.closest('[data-block-idx],[data-field-id]') as HTMLElement | null;
      idoc.querySelectorAll('.mz-selected').forEach((n) => n.classList.remove('mz-selected'));
      if (!el) { closePopover(); return; }
      if (pending) return; // resolve the pending edit first
      el.classList.add('mz-selected');
      const pageIdx = Number(el.getAttribute('data-page-idx'));
      const bi = el.getAttribute('data-block-idx');
      const fid = el.getAttribute('data-field-id');
      const t: Target = bi != null ? { pageIdx, blockIdx: Number(bi) } : { pageIdx, fieldId: String(fid) };
      const frameRect = iframeRef.current!.getBoundingClientRect();
      setTarget(t);
      setPos({ x: Math.min(frameRect.left + (e as MouseEvent).clientX + 12, window.innerWidth - 360), y: frameRect.top + (e as MouseEvent).clientY });
      setDescribe(''); setErr(null);
    });
  }, [pending, closePopover]);

  // Re-attach the listener whenever the html changes (iframe reloads srcDoc).
  useEffect(() => { /* onLoad handles attach */ }, [html]);

  const node = target ? nodeAt(doc, target) : undefined;

  async function applyDirect(patch: PatchOp[]) {
    if (!patch.length) { closePopover(); return; }
    setBusy('apply'); setErr(null);
    try {
      const r = await patchDirect(session.name, patch);
      if (!r.ok) { setErr(r.error || 'that edit was not valid'); return; }
      setPending(r); setPos(null); setTarget(null);
    } catch { setErr('the edit failed'); } finally { setBusy(null); }
  }
  async function askAi() {
    if (!target || !describe.trim()) return;
    setBusy('ask'); setErr(null);
    try {
      const r = await patchAi(session.name, target, describe.trim());
      if (!r.ok) { setErr(r.error || 'the editor could not make that change'); return; }
      setPending(r); setPos(null); setTarget(null); setDescribe('');
    } catch { setErr('the edit failed'); } finally { setBusy(null); }
  }
  async function accept() {
    if (!pending?.patch) return;
    setBusy('commit');
    try {
      const s = await commitPatch(session.name, pending.patch, noteFor(pending), pending.source ?? 'direct');
      onChange(s); setPending(null); closePopover();
    } catch { setErr('could not save'); } finally { setBusy(null); }
  }
  async function goToRev(seq: number) {
    if (seq === session.headSeq) return;
    setBusy('checkout'); discardPending();
    try { onChange(await checkoutRev(session.name, seq)); } catch { /* ignore */ } finally { setBusy(null); }
  }

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 30, display: 'grid', gridTemplateColumns: '240px minmax(0,1fr)', background: 'var(--bg)' }}>
      {/* version rail */}
      <aside style={{ borderRight: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn" onClick={onExit} style={{ padding: '4px 9px', fontSize: 12 }}>← New</button>
          <b style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={session.doc.title}>{session.doc.title}</b>
        </div>
        <div className="sec-label" style={{ padding: '12px 14px 6px' }}>Versions</div>
        <div style={{ overflowY: 'auto', padding: '0 8px 12px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {session.history.revisions.map((r) => {
            const head = r.seq === session.headSeq;
            return (
              <button key={r.seq} onClick={() => goToRev(r.seq)} disabled={busy === 'checkout'}
                style={{ textAlign: 'left', padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${head ? 'var(--accent)' : 'var(--border)'}`,
                  background: head ? 'var(--accent-soft)' : 'var(--surface-2)', color: head ? 'var(--accent)' : 'var(--text)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: head ? 'var(--accent)' : 'var(--border-strong)' }} />
                  <span className="font-mono" style={{ fontSize: 11 }}>v{r.seq}</span>
                  {r.source === 'ai' && <span className="chip" style={{ fontSize: 9, padding: '1px 5px', background: 'var(--ai-soft)', color: 'var(--ai)' }}>AI</span>}
                </div>
                <div style={{ fontSize: 12, marginTop: 2, lineHeight: 1.3 }}>{r.note || (r.kind === 'create' ? 'Authored' : 'Edit')}</div>
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: 'auto', padding: 12, borderTop: '1px solid var(--border)', fontSize: 11.5, color: 'var(--text-dim)', lineHeight: 1.45 }}>
          Click any element in the page to edit it — or describe a change and the AI will make it. Every edit previews before it saves.
        </div>
      </aside>

      {/* canvas */}
      <div style={{ position: 'relative', minWidth: 0, overflow: 'auto', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column' }}>
        {pending && (
          <div style={{ position: 'sticky', top: 0, zIndex: 5, display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
            background: 'var(--accent)', color: 'var(--accent-ink)' }}>
            <b style={{ fontSize: 13 }}>Preview{pending.source === 'ai' ? ' · AI edit' : ''}</b>
            <span style={{ fontSize: 12, opacity: 0.9, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summarize(pending)}</span>
            <button className="btn" onClick={accept} disabled={busy === 'commit'}
              style={{ padding: '5px 12px', fontSize: 12.5, background: 'var(--accent-ink)', color: 'var(--accent)', borderColor: 'var(--accent-ink)', fontWeight: 700 }}>
              {busy === 'commit' ? 'Saving…' : '✓ Accept'}
            </button>
            <button className="btn" onClick={discardPending} disabled={busy === 'commit'}
              style={{ padding: '5px 12px', fontSize: 12.5, background: 'transparent', color: 'var(--accent-ink)', borderColor: 'var(--accent-ink)' }}>Discard</button>
          </div>
        )}
        <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
          <iframe
            ref={iframeRef}
            title="one-pager"
            srcDoc={html}
            onLoad={onIframeLoad}
            style={{ width: 816, minHeight: '90vh', border: '1px solid var(--border)', borderRadius: 8, background: '#fff', boxShadow: '0 12px 44px -14px rgba(20,17,13,.24)' }}
          />
        </div>
      </div>

      {/* block popover */}
      {target && pos && node !== undefined && !pending && (
        <div style={{ position: 'fixed', left: pos.x, top: Math.min(pos.y, window.innerHeight - 320), zIndex: 20, width: 340,
          background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 12, boxShadow: '0 18px 50px -16px rgba(20,17,13,.4)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
            <span className="sec-label" style={{ color: 'var(--accent)' }}>{targetLabel(doc, target)}</span>
            <span style={{ flex: 1 }} />
            <button onClick={closePopover} aria-label="Close" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-dim)', fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
          <NodeControls
            node={node}
            path={targetPath(target)}
            busy={busy === 'apply'}
            onApply={applyDirect}
          />
          <div style={{ borderTop: '1px solid var(--border)', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label className="sec-label" style={{ color: 'var(--ai)' }}>Describe a change</label>
            <textarea className="input" rows={2} value={describe} placeholder="e.g. make it punchier, shorter, more specific…"
              onChange={(e) => setDescribe(e.target.value)} style={{ resize: 'vertical', fontSize: 13 }} />
            <button className="btn" onClick={askAi} disabled={busy === 'ask' || !describe.trim()}
              style={{ background: 'var(--ai-soft)', color: 'var(--ai)', borderColor: 'var(--ai)', fontWeight: 600, justifyContent: 'center' }}>
              {busy === 'ask' ? 'Asking…' : '✦ Ask AI'}
            </button>
          </div>
          {err && <div style={{ padding: '8px 12px', color: 'var(--danger)', background: 'var(--danger-soft)', fontSize: 12 }}>{err}</div>}
        </div>
      )}
    </div>
  );
}

function noteFor(p: PatchResult): string {
  return summarize(p);
}
function summarize(p: PatchResult): string {
  const op = p.patch?.[0];
  if (!op) return 'Edit';
  const leaf = op.path.split('/').filter(Boolean).slice(-2).join(' ');
  const verb = p.source === 'ai' ? 'AI edit' : 'Edited';
  return `${verb} ${leaf}`;
}

/** Data-driven controls for a node (string field, or object with string/array/enum props). */
function NodeControls({ node, path, busy, onApply }: {
  node: unknown;
  path: string;
  busy: boolean;
  onApply: (patch: PatchOp[]) => void;
}) {
  // Local editable copy, re-seeded whenever the target node changes.
  const initial = useMemo(() => structuredClone(node), [node]);
  const [draft, setDraft] = useState<unknown>(initial);
  useEffect(() => { setDraft(structuredClone(node)); }, [node]);

  const isString = typeof initial === 'string';
  const obj = (!isString && initial && typeof initial === 'object' ? (draft as Record<string, unknown>) : null);
  const objInit = (!isString && initial && typeof initial === 'object' ? (initial as Record<string, unknown>) : null);

  function buildPatch(): PatchOp[] {
    if (isString) {
      return draft !== initial ? [{ op: 'replace', path, value: draft }] : [];
    }
    if (!obj || !objInit) return [];
    const ops: PatchOp[] = [];
    for (const key of Object.keys(obj)) {
      if (key === 'kind' || key === 'layout') continue;
      if (JSON.stringify(obj[key]) !== JSON.stringify(objInit[key])) {
        ops.push({ op: 'replace', path: `${path}/${key}`, value: obj[key] });
      }
    }
    return ops;
  }

  const set = (key: string, value: unknown) => setDraft((d: unknown) => ({ ...(d as Record<string, unknown>), [key]: value }));

  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {isString && (
        <Field label="text">
          <textarea className="input" rows={3} value={String(draft ?? '')} onChange={(e) => setDraft(e.target.value)} style={{ resize: 'vertical', fontSize: 13 }} />
        </Field>
      )}
      {obj && Object.keys(obj).filter((k) => k !== 'kind' && k !== 'layout' && k !== 'ref').map((key) => {
        const val = obj[key];
        if (key === 'width') {
          return (
            <Field key={key} label="width">
              <select className="input" value={String(val ?? 'full')} onChange={(e) => set(key, e.target.value)} style={{ fontSize: 13 }}>
                <option value="full">Full width</option>
                <option value="half">Half width</option>
              </select>
            </Field>
          );
        }
        if (Array.isArray(val)) {
          return (
            <Field key={key} label={key}>
              <ListEditor items={val as string[]} onChange={(items) => set(key, items)} />
            </Field>
          );
        }
        if (typeof val === 'string' || val == null) {
          const long = key === 'text' || key === 'body' || (typeof val === 'string' && val.length > 48);
          return (
            <Field key={key} label={key}>
              {long
                ? <textarea className="input" rows={3} value={String(val ?? '')} onChange={(e) => set(key, e.target.value)} style={{ resize: 'vertical', fontSize: 13 }} />
                : <input className="input" value={String(val ?? '')} onChange={(e) => set(key, e.target.value)} style={{ fontSize: 13 }} />}
            </Field>
          );
        }
        return null;
      })}
      <button className="btn btn-primary" onClick={() => onApply(buildPatch())} disabled={busy}
        style={{ justifyContent: 'center' }}>
        {busy ? 'Applying…' : 'Apply changes'}
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="font-mono" style={{ fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>{label}</span>
      {children}
    </label>
  );
}

function ListEditor({ items, onChange }: { items: string[]; onChange: (items: string[]) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', gap: 5 }}>
          <input className="input" value={it} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} style={{ fontSize: 12.5 }} />
          <button className="btn" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Remove" style={{ padding: '0 8px', color: 'var(--danger)' }}>–</button>
        </div>
      ))}
      <button className="btn" onClick={() => onChange([...items, 'New point'])} style={{ fontSize: 12, padding: '4px 8px' }}>+ Add</button>
    </div>
  );
}
